import React, {useState, useRef, useMemo, useCallback} from 'react';
import {useCountDown} from 'ahooks';
import FissionBanner from '../components/fission/FissionBanner';

interface CountdownDisplayProps {
  cycleEndDate?: string | number;
  onCountdownEnd?: () => void;
}

const CountdownDisplay = ({
  cycleEndDate,
  onCountdownEnd,
}: CountdownDisplayProps) => {
  const [countdown] = useCountDown({
    targetDate: cycleEndDate ? new Date(cycleEndDate) : undefined,
    onEnd: () => {
      onCountdownEnd?.();
    },
  });

  if (!cycleEndDate || countdown <= 0) {
    return null;
  }

  const hours = Math.floor(countdown / (1000 * 60 * 60));
  const minutes = Math.floor((countdown % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((countdown % (1000 * 60)) / 1000);

  return (
    <div className="flex justify-center">
      <div className="text-center pt-1 relative inline-block -top-3 rounded-b-xl bg-[rgba(44,32,10,0.08)] px-8">
        <div className="text-[#F67918] text-2xl font-bold">
          {String(hours).padStart(2, '0')}:{String(minutes).padStart(2, '0')}:
          {String(seconds).padStart(2, '0')}
        </div>
      </div>
    </div>
  );
};
import FissionTabs from '../components/fission/FissionTabs';
import FissionTop from '../components/fission/FissionTop';
import FissionLeaderboardList from '../components/fission/FissionLeaderboardList';
import FissionUserRank from '../components/fission/FissionUserRank';
import DetailNavTitle from '@businessComponents/detail-nav-title';
import {goBack} from '@utils';
import {useFissionBoard} from '../hooks/useFissionBoard';
import Rule from '@/common-pages/leaderboard/components/rule';

const TABS_CONFIG = [
  {
    label: 'This Round',
    key: 'current',
  },
  {
    label: 'Last Round',
    key: 'previous',
  },
];

interface FissionBoardProps {
  label?: string;
}

function FissionBoard({label = ''}: FissionBoardProps) {
  const [activeTab, setActiveTab] = useState('current');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const {
    rankingList,
    currentCycle,
    jackpot,
    loading,
    myRanking,
    myRankingLoading,
    switchToPrevious,
    switchToCurrent,
    cycleEndDate,
    refresh,
  } = useFissionBoard();

  const tabItems = useMemo(() => TABS_CONFIG, []);

  const handleTabChange = useCallback(
    (activeKey: string) => {
      setActiveTab(activeKey);
      if (activeKey === 'current') {
        switchToCurrent();
      } else {
        switchToPrevious();
      }
    },
    [switchToCurrent, switchToPrevious],
  );

  return (
    <div className="fissionboard-bg relative h-screen flex flex-col overflow-hidden translate-x-0">
      <DetailNavTitle
        title={label}
        hideServer
        hideAmount
        rightNode={<Rule gameName="FISSION_RANK_ACTIVITY" />}
        onBack={() => {
          goBack();
        }}
      />
      <div className="relative z-10">
        <div
          className="flex justify-center mt-3 bg-inherit"
          role="tablist"
          aria-label="Period selection">
          <FissionTabs
            items={tabItems}
            activeKey={activeTab}
            onChange={handleTabChange}
          />
        </div>

        <div aria-label="Jackpot information" className="relative">
          <FissionBanner
            amount={jackpot > 0 ? jackpot : undefined}
            currentCycle={currentCycle}
          />
          {cycleEndDate && cycleEndDate > Date.now() ? (
            <CountdownDisplay
              cycleEndDate={cycleEndDate}
              onCountdownEnd={() => refresh(null)}
            />
          ) : null}
        </div>
      </div>

      <div
        ref={scrollContainerRef}
        className="overflow-y-auto touch-pan-y flex-1"
        role="region"
        aria-label="Leaderboard scrollable content">
        <div className="z-10 relative pb-[4rem]">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white" />
            </div>
          ) : (
            <>
              <FissionTop data={rankingList.slice(0, 3)} />
              <FissionLeaderboardList data={rankingList} />
            </>
          )}
        </div>
      </div>

      <FissionUserRank myRanking={myRanking} loading={myRankingLoading} />
    </div>
  );
}

export default FissionBoard;
